import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Pencil, Warehouse } from "lucide-react";
import { FormModal, Field, SectionTitle } from "@/pages/InwardStock";

const emptyForm = { name: "", code: "", address: "", phone: "", notes: "", is_active: true };

export default function Locations() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => setItems((await api.get("/locations")).data);
  useEffect(() => { load(); }, []);

  const close = () => { setShowForm(false); setEditingId(null); setForm(emptyForm); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) { await api.patch(`/locations/${editingId}`, form); toast.success("Location updated"); }
      else { await api.post("/locations", form); toast.success("Location added"); }
      close(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setForm({ name: v.name, code: v.code, address: v.address || "", phone: v.phone || "", notes: v.notes || "", is_active: v.is_active !== false });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    try { await api.delete(`/locations/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="locations-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-2"><span className="inline-block w-6 h-[2px] bg-[#28B516] mr-2 align-middle" />Warehouses</div>
          <h1 className="font-display text-4xl uppercase text-zinc-900">Stock Locations</h1>
          <div className="text-xs text-zinc-500 mt-2">Create warehouses / showrooms / service centers where stock is held</div>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-location-btn"><Plus className="w-4 h-4" /> New Location</button>
      </div>

      <div className="border border-zinc-200 bg-white rounded-2xl overflow-x-auto">
        <table className="data-table" data-testid="locations-table">
          <thead><tr><th>Code</th><th>Name</th><th>Phone</th><th>Address</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="6" className="text-center py-12 text-zinc-500"><Warehouse className="w-10 h-10 mx-auto mb-3 opacity-40" />No locations yet.</td></tr>
            )}
            {items.map((v) => (
              <tr key={v.id}>
                <td className="font-mono font-bold text-[#28B516]">{v.code}</td>
                <td className="font-semibold text-zinc-900">{v.name}</td>
                <td>{v.phone || "—"}</td>
                <td className="text-zinc-500">{v.address || "—"}</td>
                <td>{v.is_active ? <span className="badge-ok">Active</span> : <span className="badge-warn">Inactive</span>}</td>
                <td>
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(v)} className="text-zinc-400 hover:text-[#28B516] transition-colors" data-testid={`edit-location-${v.id}`} title="Edit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(v.id)} className="text-zinc-400 hover:text-[#D9363E] transition-colors" data-testid={`del-location-${v.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <FormModal onClose={close}>
          <form onSubmit={submit} className="space-y-5" data-testid="location-form">
            <div className="flex items-center justify-between border-b border-zinc-200 pb-4">
              <h2 className="font-display text-2xl uppercase text-zinc-900">{editingId ? "Edit Location" : "New Location"}</h2>
              <button type="button" onClick={close} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5" /></button>
            </div>
            <SectionTitle>Location Details</SectionTitle>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name*"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" data-testid="loc-name" placeholder="e.g. Main Warehouse, Bengaluru" /></Field>
              <Field label="Code*"><input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="input-field font-mono" data-testid="loc-code" placeholder="e.g. BLR-WH1" /></Field>
              <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-field font-mono" data-testid="loc-phone" /></Field>
              <Field label="Active">
                <select value={form.is_active ? "1" : "0"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "1" })} className="input-field" data-testid="loc-active">
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </Field>
              <Field label="Address" full><textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input-field h-20 resize-none" data-testid="loc-address" /></Field>
              <Field label="Notes" full><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-field h-20 resize-none" data-testid="loc-notes" /></Field>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
              <button type="button" onClick={close} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="submit-location">{editingId ? "Update" : "Create"}</button>
            </div>
          </form>
        </FormModal>
      )}
    </div>
  );
}
